import { routes, type NewsletterSection } from "@ckgo2/shared";
import i18n from "@/i18n";

export type { NewsletterSection };

/**
 * React-query cache keys shared ACROSS the studio's component boundary (the page owns the
 * drafts query, QueuePanel invalidates it after unschedule/send-now). Single source so a key
 * rename can't silently turn a cross-file invalidation into a no-op.
 */
export const studioKeys = {
  drafts: ["studio-drafts"] as const,
  queue: ["studio-queue"] as const,
  results: ["studio-results"] as const,
};
export interface NewsletterSubjectVariant {
  subject: string;
  preheader: string;
}

/** Campaign row fields the studio cares about (additive Řez 4 columns). */
export interface StudioCampaign {
  id: string;
  subject: string;
  status: "draft" | "generated" | "scheduled" | "sending" | "sent";
  preheader: string | null;
  themeKey: string | null;
  sectionsJson: NewsletterSection[] | null;
  subjectVariants: NewsletterSubjectVariant[] | null;
  generationRunId: string | null;
  aiPrompt: string | null;
  sentCount: number;
  openedCount: number;
  clickedCount: number;
  sentAt: string | null;
  createdAt: string;
}

export interface GenerationRun {
  id: string;
  isoWeek: string;
  status: "running" | "done" | "failed" | "aborted";
  trigger: "cron" | "manual";
  tokensUsed: number;
  error: string | null;
  startedAt: string;
  finishedAt: string | null;
  steps?: Array<{
    step: string;
    status: "running" | "done" | "failed";
    tokens: number;
    error: string | null;
    updatedAt: string;
  }>;
}

export interface Directive {
  id: string;
  kind: "inspiration" | "assignment";
  text: string;
  verbatimText: string | null;
  verbatimSection: string | null;
  status: "pending" | "consumed" | "archived";
  createdAt: string;
}

export interface Persona {
  id: string;
  name: string;
  description: string;
  source: "manual" | "discovered";
  needsReview: boolean;
  isActive: boolean;
  traits: Record<string, unknown> | null;
  createdAt: string;
}

export interface CalendarEntry {
  id: string;
  dateFrom: string;
  dateTo: string;
  label: string;
  note: string | null;
}

/** GET /newsletter-studio/queue — scheduled + in-flight campaigns for the Queue tab. */
export interface QueueData {
  ratePerMinute: number;
  wave2DelayHours: number;
  scheduled: Array<{
    id: string;
    subject: string;
    scheduledAt: string | null;
    isAi: boolean;
    segmentId: string | null;
    segmentName: string | null;
  }>;
  sending: Array<{
    id: string;
    subject: string;
    sentAt: string | null;
    isAi: boolean;
    waveState: {
      wave1At?: string;
      wave1Count?: number;
      winnerVariant?: number;
      wave2At?: string;
      segmentId?: string | null;
    } | null;
    counts: { total: number; sent: number; failed: number; queued: number; wave2Queued: number };
  }>;
}

export const THEME_OPTIONS = [
  { key: "violet", label: i18n.t("newsletter.themes.violet"), accent: "#be016d" },
  { key: "magenta", label: i18n.t("newsletter.themes.magenta"), accent: "#e2007a" },
  { key: "jade", label: i18n.t("newsletter.themes.jade"), accent: "#0f5e58" },
  { key: "sunset", label: i18n.t("newsletter.themes.sunset"), accent: "#c0892f" },
];

export const SECTION_LABELS: Record<string, string> = {
  bar: i18n.t("newsletter.sections.labels.bar"),
  hero: i18n.t("newsletter.sections.labels.hero"),
  intro: i18n.t("newsletter.sections.labels.intro"),
  trustBoxes: i18n.t("newsletter.sections.labels.trustBoxes"),
  spotlight: i18n.t("newsletter.sections.labels.spotlight"),
  tripCards: i18n.t("newsletter.sections.labels.tripCards"),
  review: i18n.t("newsletter.sections.labels.review"),
  included: i18n.t("newsletter.sections.labels.included"),
  video: i18n.t("newsletter.sections.labels.video"),
  routeTimeline: i18n.t("newsletter.sections.labels.routeTimeline"),
  combos: i18n.t("newsletter.sections.labels.combos"),
  note: i18n.t("newsletter.sections.labels.note"),
  helpDesk: i18n.t("newsletter.sections.labels.helpDesk"),
  footer: i18n.t("newsletter.sections.labels.footer"),
};

/** Default skeletons for the "add section" menu. */
export function defaultSection(type: string): NewsletterSection {
  switch (type) {
    case "bar":
      return {
        type: "bar",
        left: "BANH MI ESCAPE / VIETNAM",
        right: `VOL. 1 · ${new Date().getFullYear()}`,
      };
    case "hero":
      return {
        type: "hero",
        mediaId: null,
        eyebrow: "★ This week",
        title: "Vietnam.",
        titleAccent: "Your way.",
        subtitle: "",
      };
    case "intro":
      return { type: "intro", greeting: "Hello {{firstName}},", body: "Dear traveler, ..." };
    case "trustBoxes":
      return {
        type: "trustBoxes",
        items: [
          { label: "GUIDES", value: "English-speaking" },
          { label: "GROUPS", value: "6–14 people" },
          { label: "PROMISE", value: "best local price" },
        ],
      };
    case "spotlight":
      return {
        type: "spotlight",
        variant: "guide",
        title: "",
        body: "",
        mediaId: null,
        bullets: [],
      };
    case "tripCards":
      return {
        type: "tripCards",
        mode: "personalized",
        tripIds: [],
        count: 3,
        discoverySlot: true,
        hooks: {},
      };
    case "review":
      return { type: "review", quote: "", author: "", context: "" };
    case "included":
      return { type: "included", included: [""], excluded: [""] };
    case "video":
      return {
        type: "video",
        eyebrow: "Before you decide",
        title: "See what it's like",
        youtubeUrl: "https://youtu.be/",
        mediaId: null,
        caption: "",
      };
    case "routeTimeline":
      return { type: "routeTimeline", title: "", stops: ["Hanoi", "Hoi An"] };
    case "combos":
      return {
        type: "combos",
        items: [
          {
            title: "",
            subtitle: "",
            price: "on request",
            linkUrl: `https://ckgo2.com${routes.zajezdy()}`,
          },
        ],
      };
    case "note":
      return { type: "note", body: "", tone: "info" };
    case "helpDesk":
      return {
        type: "helpDesk",
        title: "Can't decide?",
        body: "Write or call — we'll hold a seat and help you choose.",
        ctaLabel: "WRITE TO US →",
        ctaUrl: `https://ckgo2.com${routes.kontakt()}`,
        phone: "+420 793 950 220",
      };
    default:
      return { type: "footer" } as NewsletterSection;
  }
}
